import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { type Theme } from '@mui/material/styles';
import LoginIcon from '@mui/icons-material/Login';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import { apiGet } from '../api/client';
import { fetchSmsCabinetCheck } from '../api/smsCabinetCheck';
import { clearAuthToken, getSmsCabinetUrl, getSmsSignUpUrl, getStoredAuthToken } from '../auth/cascadeAuth';

function authGateShellSx(theme: Theme): { bgcolor: string; backgroundImage: string } {
  return {
    bgcolor: 'background.default',
    backgroundImage:
      theme.palette.mode === 'dark'
        ? `
        radial-gradient(ellipse 90% 55% at 50% -15%, rgba(100,181,246,0.12), transparent 52%),
        radial-gradient(ellipse 65% 40% at 100% 100%, rgba(100,181,246,0.05), transparent 45%)
      `
        : `
        radial-gradient(ellipse 90% 55% at 50% -15%, rgba(25,118,210,0.14), transparent 52%),
        radial-gradient(ellipse 65% 40% at 100% 100%, rgba(25,118,210,0.06), transparent 45%),
        radial-gradient(ellipse 55% 35% at 0% 100%, rgba(25,118,210,0.06), transparent 45%)
      `,
  };
}

/** Доступ только после успешного /api/auth/me (cookie userId или Bearer). */
const RequireAuth: React.FC = () => {
  const { t } = useTranslation();
  const [state, setState] = useState<'loading' | 'ok' | 'no' | 'misconfigured' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await apiGet<{ userId: number }>('/api/auth/me');
        if (cancelled) return;
        const tok = getStoredAuthToken()?.trim();
        if (tok) {
          try {
            await fetchSmsCabinetCheck();
          } catch (e: unknown) {
            if (cancelled) return;
            const st = (e as Error & { status?: number }).status;
            if (st === 401) {
              setState('no');
              return;
            }
            setState('error');
            return;
          }
        }
        if (!cancelled) setState('ok');
      } catch (e: unknown) {
        const st = (e as Error & { status?: number }).status;
        if (st === 401) {
          if (!cancelled) setState('no');
        } else if (st === 503) {
          if (!cancelled) setState('misconfigured');
        } else if (!cancelled) {
          setState('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === 'loading') {
    return (
      <Box
        sx={(theme) => ({
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          ...authGateShellSx(theme),
        })}
      >
        <CircularProgress size={40} thickness={4} />
      </Box>
    );
  }

  if (state === 'no') {
    const cabinet = getSmsCabinetUrl();
    const signUp = getSmsSignUpUrl();
    return (
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 2,
          ...authGateShellSx(theme),
        })}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: 440,
            width: '100%',
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            border: 1,
            borderColor: 'divider',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
          }}
        >
          <Avatar
            sx={{
              mx: 'auto',
              mb: 2,
              width: 64,
              height: 64,
              bgcolor: 'primary.main',
              boxShadow: (th) => `0 4px 20px ${th.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(25,118,210,0.35)'}`,
            }}
          >
            <LockRoundedIcon sx={{ fontSize: 34 }} aria-hidden />
          </Avatar>
          <Typography variant="h5" component="h1" fontWeight={700} gutterBottom>
            {t('auth.requiredTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
            {t('auth.requiredHint')}
          </Typography>
          <Stack spacing={1.25}>
            {cabinet ? (
              <Button
                variant="contained"
                color="primary"
                size="large"
                href={cabinet}
                component="a"
                fullWidth
                startIcon={<LoginIcon />}
                onClick={() => {
                  clearAuthToken();
                }}
                sx={{
                  py: 1.25,
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem',
                }}
              >
                {t('auth.enterCabinet')}
              </Button>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {t('auth.noCabinetConfigured')}
              </Typography>
            )}
            <Button
              variant="outlined"
              size="medium"
              fullWidth
              component="a"
              href={signUp}
              rel="noopener noreferrer"
              target="_blank"
              startIcon={<PersonAddAlt1RoundedIcon />}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              {t('auth.registerSms')}
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (state === 'misconfigured') {
    return (
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 2,
          ...authGateShellSx(theme),
        })}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: 480,
            width: '100%',
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            border: 1,
            borderColor: 'divider',
            textAlign: 'center',
          }}
        >
          <Avatar
            sx={{
              mx: 'auto',
              mb: 2,
              width: 56,
              height: 56,
              bgcolor: 'warning.light',
              color: 'warning.contrastText',
            }}
          >
            <SettingsEthernetIcon />
          </Avatar>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            {t('auth.serverAuthDisabled')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'left' }}>
            {t('auth.serverAuthDisabledHint')}
          </Typography>
          <Button variant="outlined" fullWidth sx={{ borderRadius: 2, textTransform: 'none' }} onClick={() => window.location.reload()}>
            {t('auth.refresh')}
          </Button>
        </Paper>
      </Box>
    );
  }

  if (state === 'error') {
    return (
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 2,
          ...authGateShellSx(theme),
        })}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: 440,
            width: '100%',
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            border: 1,
            borderColor: 'error.light',
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" fontWeight={700} gutterBottom color="error">
            {t('auth.checkFailed')}
          </Typography>
          <Button variant="contained" color="primary" fullWidth sx={{ mt: 2, borderRadius: 2, textTransform: 'none' }} onClick={() => window.location.reload()}>
            {t('auth.refresh')}
          </Button>
        </Paper>
      </Box>
    );
  }

  return <Outlet />;
};

export default RequireAuth;
